// src/utils/orgPayments.js
//
// Web-only helpers for reading an organisation's payment-readiness state.
// NOT mirrored to mobile — no mirror obligation is created by this file.
//
// isOrgStripeReady mirrors the server-side Stripe-onboarding gate EXACTLY so
// the client UX guard can never drift from what the Cloud Functions enforce.
// Both server gates (getDepositPaymentLink / createPaymentIntent in
// functions/index.js) use the same condition:
//   const stripeAccountId = orgData?.stripeConnectedAccountId || orgData?.stripeAccountId;
//   if (!stripeAccountId || !orgData.stripeOnboardingComplete) { throw ... }
// So "onboarded" = (stripeConnectedAccountId || stripeAccountId) AND
// stripeOnboardingComplete === true. The client is a UX layer in front of the
// server gate, never a replacement for it.
export const isOrgStripeReady = (org) =>
    !!((org?.stripeConnectedAccountId || org?.stripeAccountId) && org?.stripeOnboardingComplete);

// ─────────────────────────────────────────────────────────────────────────────
// payhide-7e4c1b05 — WEB MIRROR of functions/utils/stripeChargeStatus.js
// (canCharge, isOrgPayRestricted, canOrgTakeOnlinePayments). KEEP IN STEP with the server
// file: functions/scripts/testOrgPaymentsMirror.js runs one table of orgs through both copies
// and fails on any disagreement. This file must stay IMPORT-FREE so that script can load it.
//
//   canOrgTakeOnlinePayments  customer-facing: the staff "Pay Online" tab, payment notes.
//                             The old gate AND NOT restricted.
//   isOrgPayRestricted        operator-only: the org HAS a Stripe account and its stored
//                             snapshot (for that same account) says it cannot charge.
//   restrictedBannerKind      which Settings → Payments banner to show.
// ─────────────────────────────────────────────────────────────────────────────

// A missing card_payments capability defers to charges_enabled; a reported one must be 'active'.
export const canCharge = (snapshot) => {
    if (!snapshot || typeof snapshot !== 'object') return false;
    if (snapshot.chargesEnabled !== true) return false;
    return snapshot.cardPayments == null || snapshot.cardPayments === 'active';
};

const storedAccountIdOf = (org) => (org && (org.stripeConnectedAccountId || org.stripeAccountId)) || null;

// The snapshot only counts when it describes the org's CURRENT account.
const currentSnapshotOf = (org) => {
    const accountId = storedAccountIdOf(org);
    const snap = org && org.stripeChargeStatus;
    if (!accountId || !snap || typeof snap !== 'object') return null;
    return snap.accountId === accountId ? snap : null;
};

export const isOrgPayRestricted = (org) => {
    const snap = currentSnapshotOf(org);
    return !!snap && !canCharge(snap);
};

export const canOrgTakeOnlinePayments = (org) => {
    const legacyReady = !!(storedAccountIdOf(org) && org.stripeOnboardingComplete);
    return legacyReady && !isOrgPayRestricted(org);
};

// Settings → Payments banner (Seam 5b). null when not restricted. Otherwise:
//   'unfinished' -- details never submitted (Seam 4 email kind 'unfinished')
//   'reviewing'  -- submitted and Stripe asks for nothing: it is still verifying. The same
//                   condition as Seam 4's pending-verification guard, which sends no email.
//   'paused'     -- submitted and Stripe wants more (Seam 4 email kind 'paused')
export const restrictedBannerKind = (org) => {
    if (!isOrgPayRestricted(org)) return null;
    const snap = currentSnapshotOf(org);
    if (snap.detailsSubmitted !== true) return 'unfinished';
    const due = (Array.isArray(snap.currentlyDue) ? snap.currentlyDue.length : 0)
        + (Array.isArray(snap.pastDue) ? snap.pastDue.length : 0);
    return due === 0 ? 'reviewing' : 'paused';
};

// ─────────────────────────────────────────────────────────────────────────────
// tmplpay-5f1d8b23 — WEB MIRROR of two strings in functions/utils/emailPayWording.js, for the
// Settings → Email Templates preview (EmailTemplatesSettings.js). The preview must show the
// invoice email's real button and the real "view (and pay) online" sentence:
//   button          = CAN_PAY / CANNOT_PAY .invoiceButton          (sendInvoiceEmail's button)
//   defaultBodyLine = CAN_PAY / CANNOT_PAY .invoiceDefaultBodyLine  (the server's own default
//                     body, sent when the org has no saved invoice template)
// functions/scripts/testOrgPaymentsMirror.js (part E) fails if either copy drifts.
// ─────────────────────────────────────────────────────────────────────────────
export const INVOICE_EMAIL_WORDING = Object.freeze({
    canPay: Object.freeze({
        button: 'View & Pay Online',
        defaultBodyLine: 'You can view and pay it online using the button below.',
    }),
    cannotPay: Object.freeze({
        button: 'View Invoice Online',
        defaultBodyLine: 'You can view it online using the button below.',
    }),
});

export const invoiceEmailWordingFor = (canPayOnline) =>
    (canPayOnline ? INVOICE_EMAIL_WORDING.canPay : INVOICE_EMAIL_WORDING.cannotPay);
